import { getHTMLFromFragment } from '@tiptap/core';
import { fixTables } from '@tiptap/pm/tables';
import { Editor } from '@tiptap/react';
import { fromEditorHtml, toEditorHtml } from './placeholderMarkup';
import { DEFAULT_RICH_TEXT_CONFIG, richTextExtensions, type RichTextConfig } from './richTextExtensions';

const LINE_BOUNDARY = 'p, h1, h2, h3, h4, h5, h6, li, blockquote, pre, hr, br, table, tr, th, td';
const WHITESPACE_RUN = /[ \t\n\r\f]+/g;

const collapseUnrenderedWhitespace = (body: HTMLElement): void => {
    const walker = body.ownerDocument.createTreeWalker(body, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
    let previous: Text | null = null;
    let atLineStart = true;
    const endLine = () => {
        if (previous) previous.data = previous.data.replace(/ $/, '');
        previous = null;
        atLineStart = true;
    };
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (node.nodeType === Node.ELEMENT_NODE) {
            if ((node as Element).matches(LINE_BOUNDARY)) endLine();
            continue;
        }
        if (node.parentElement?.closest('pre')) continue;
        const text = node as Text;
        text.data = text.data.replace(WHITESPACE_RUN, ' ');
        if (atLineStart || previous?.data.endsWith(' ')) text.data = text.data.replace(/^ /, '');
        previous = text;
        atLineStart = false;
    }
    endLine();
};

const normalise = (html: string): string => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const blanks: Node[] = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (!node.textContent?.trim()) blanks.push(node);
    }
    blanks.forEach((node) => node.parentNode?.removeChild(node));
    doc.querySelectorAll('li > p:only-child, td > p:only-child, th > p:only-child').forEach((paragraph) => paragraph.replaceWith(...Array.from(paragraph.childNodes)));
    collapseUnrenderedWhitespace(doc.body);
    return doc.body.innerHTML;
};

// The live editor repairs ragged tables on its first transaction, so compare against the repaired doc.
const editorHtml = (probe: Editor, config: RichTextConfig): string => {
    if (!config.features.includes('tables')) return probe.getHTML();
    const doc = fixTables(probe.state)?.doc ?? probe.state.doc;
    return getHTMLFromFragment(doc.content, probe.schema);
};

export function canRenderAsRichText(html: string, config: RichTextConfig = DEFAULT_RICH_TEXT_CONFIG): boolean {
    if (!html.trim()) return true;
    const probe = new Editor({ extensions: richTextExtensions(config), content: toEditorHtml(html, config) });
    try {
        return normalise(fromEditorHtml(editorHtml(probe, config), config)) === normalise(html);
    } finally {
        probe.destroy();
    }
}
